import {
  BehaviorSubject,
  Subject,
  Subscription,
  combineLatest,
  Observable
} from 'rxjs';

import {
  Component,
  HostListener,
  OnDestroy,
  ViewEncapsulation
} from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { filter, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';

import * as fromCompanySettingsStore from './settings/store';
import { LoadAccountInformation, LoadUserInformation } from './account/store';
import { LoadCompanySettings } from './settings/store';

import { PageLoaderService } from './shared/components/loader/page-loader.service';
import { PermissionService } from './shared/permission/permission.service';
import { SideBarService } from './shared/sidebar/sidebar.service';
import { ModalService } from './shared/components/modal/modal.service';
import * as fromStoreSettings from './settings/store';
import { TimezoneModalComponent } from './shared/components/modal/timezone-modal/timezone-modal.component';
import { CompanySettings } from './settings/model/company-settings';
import { CompanySettingsService } from './settings/services/company-settings.service';
import { ValuePair } from './shared/interfaces/ValuePair';
import { TokenService } from './authentication/token.service';
import { GoogleAnalyticsService } from './shared/analytics/google-analytics.service';
import { DownloadService } from './shared/download-service';

@Component({
  selector: 'ls-root',
  templateUrl: './app.component.html',
  styleUrls: ['../assets/stylesheets/application.scss'],
  // tslint:disable-next-line:use-view-encapsulation
  encapsulation: ViewEncapsulation.None
})
export class AppComponent implements OnDestroy {
  public isLoading$: BehaviorSubject<boolean>;
  private _sideBarStatus: Object;
  private ngUnsubscribe: Subject<Subscription> = new Subject();
  private companySettings$: Observable<CompanySettings>;

  constructor(
    private tokenService: TokenService,
    private sideBarService: SideBarService,
    private router: Router,
    private permission: PermissionService,
    private loadingService: PageLoaderService,
    private store: Store<fromCompanySettingsStore.CompanyState>,
    protected modalService: ModalService,
    private companySettingsService: CompanySettingsService,
    private googleAnalytics: GoogleAnalyticsService,
    private download: DownloadService
  ) {
    (window as any).downloadReport = (url: string, context?: string) => {
      return this.download.downloadFile(url, context);
    };
    sideBarService.statusSidebarObservable
      .pipe(
        map((sideBarStatus: Object) => {
          this._sideBarStatus = sideBarStatus;
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe();
    /*
    I am not sure why this filter was here in the first place. If we made a bug because of this, we should change the CanDeactivateGuards
    to throw an error with a custom reason and not redirect when that reason is found
    https://stackblitz.com/edit/angular-cancel-reason?file=src%2Fapp%2Fauth.guard.ts


    this.router.events.pipe(filter((event: NavigationCancel) => event instanceof NavigationCancel)).subscribe((event: NavigationCancel) => {
      const parent: string = event.url
        .split('/')
        .slice(0, -1)
        .join('/');
      if (parent && this.isLoggedIn()) {
        this.router.navigateByUrl(parent);
      }
    });
    */

    if (!this.isLoading$) {
      this.isLoading$ = this.loadingService.pageLoading$;
    }

    this.tokenService.tokenChanged$
      .pipe(
        tap((token: string) => {
          if (!!token) {
            this.store.dispatch(new LoadCompanySettings());
            this.store.dispatch(new LoadAccountInformation());
            this.store.dispatch(new LoadUserInformation());
            this.checkTimeZone();
            this.googleAnalytics.startTracking();
          } else {
            this.googleAnalytics.stopTracking();
          }
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe();
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  @HostListener('window:resize', ['$event'])
  public onResize(): void {
    const width: number = window.document.body.clientWidth || window.innerWidth;
    if (width < 815) {
      this.sideBarService.close();
    } else {
      this.sideBarService.reset();
    }
  }

  @HostListener('window:load', ['$event'])
  public onLoad(): void {
    const width: number = window.document.body.clientWidth || window.innerWidth;
    if (width < 815) {
      this.sideBarService.close();
    } else {
      this.sideBarService.reset();
    }
  }

  @HostListener('window:message', ['$event'])
  public onPostMessage(event: any): void {
    if (event.data && event.data.messageType === 'redirect') {
      this.router.navigate([event.data.url]);
    }

    if (event.data && event.data.messageType === 'toggle sidebar') {
      this.sideBarService.toggle();
    }

    if (
      event.data &&
      event.data.messageType &&
      event.data.messageType.toLowerCase() === 'change reports navigation'
    ) {
      this.router.navigateByUrl(
        `/reports${event.data.url.replace('/payments', '')}`
      );
    }
  }

  public isLoggedIn(): boolean {
    return this.tokenService.hasToken() && this.permission.hasPermissionLevel();
  }

  public get sideBarStatus(): Object {
    return this._sideBarStatus;
  }

  private checkCompanySettingsStore(): Observable<boolean> {
    return this.store.select(fromStoreSettings.getCompanySettingsLoaded).pipe(
      tap((loaded: boolean) => {
        if (!loaded) {
          this.store.dispatch(new fromStoreSettings.LoadCompanySettings());
        }
      }),
      filter((loaded: boolean) => loaded),
      take(1)
    );
  }

  private checkTimeZone(): void {
    this.companySettings$ = this.checkCompanySettingsStore().pipe(
      switchMap(() => {
        return this.store.select(fromStoreSettings.getCompanySettings);
      })
    );

    combineLatest(
      this.companySettings$,
      this.companySettingsService.getTimeZones()
    )
      .pipe(take(1))
      .subscribe(
        ([companySettings, timeZones]: [CompanySettings, Array<ValuePair>]) => {
          if (
            companySettings.timeZone === 0 &&
            companySettings.timeZoneId === 'UTC'
          ) {
            return this.modalService.open(TimezoneModalComponent, {
              companySettings: companySettings,
              timeZones: timeZones
            });
          }
        }
      );
  }
}
